import React, { useEffect, useRef, useState } from 'react'

import { useLastSeenRelease } from '../../hooks/useLastSeenRelease'
import ReleaseNotesDialog from './ReleaseNotesDialog'

/**
 * Auto-opens the release-notes dialog the first time a user lands on an unseen
 * release, replacing the old top-of-page "what's new" banner. Headless - it
 * renders only the dialog.
 *
 * Mounted once per layout (admin + inspector). A user is only ever in one
 * layout at a time, so a single launcher is live and the dialog pops once.
 * `markSeen()` runs on close (dismiss / backdrop / escape), NOT on open - a
 * release the user never actually dismissed pops again next session rather
 * than being cleared just for having been shown. `autoOpenedRef` stops the
 * effect re-opening it within the same mount.
 *
 * The version-number click (VersionDisplay) opens the same dialog on demand;
 * this component does not interfere with that.
 */
const ReleaseNotesAutoLauncher: React.FC = () => {
  const { currentVersion, unseen, loading, markSeen } = useLastSeenRelease()
  const [open, setOpen] = useState(false)
  const autoOpenedRef = useRef(false)

  useEffect(() => {
    if (autoOpenedRef.current) return
    if (!currentVersion || loading || !unseen) return
    autoOpenedRef.current = true
    setOpen(true)
  }, [currentVersion, loading, unseen])

  const handleClose = () => {
    setOpen(false)
    void markSeen()
  }

  return <ReleaseNotesDialog open={open} onClose={handleClose} />
}

export default ReleaseNotesAutoLauncher
